import { DynamicContent, DynamicContentItem } from "../Models/DynamicContent";

/** An object used to query the dynamic content panel to show specific content. */
export interface DynamicContentQuery {
  tabTitle: string;
  contentItemTitle?: string;
}

const recursivelyFindItem = (
  item: EnhancedDynamicContentItem,
  itemTitle: string
): EnhancedDynamicContentItem | undefined => {
  // if this is the direct item we are searching for, return it
  if (item.title === itemTitle) return item;
  if (item.children) {
    item.children.forEach((child, index) => {
      const foundChild = recursivelyFindItem(child, itemTitle);
      if (foundChild) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        item.children![index] = { ...child };
        item.showChildIndex = index;
        return item;
      }
    });
  }
  return undefined;
};

const recursivelySearchItem = (
  item: EnhancedDynamicContentItem,
  searchTokens?: string[]
): EnhancedDynamicContentItem => {
  item.children = item.children?.map(child => recursivelySearchItem(child, searchTokens));
  // the item is hidden if:
  // 1. search params exist &&
  // 2. it's title does not match the query &&
  // 3. it doesn't have keywords that match the query &&
  // 4. it does not have a child that is shown
  item.hidden =
    !!searchTokens &&
    !searchTokens.some(token => item.title.toLocaleLowerCase().includes(token)) &&
    !item.keywords.some(keyword => searchTokens.some(token => keyword.toLocaleLowerCase().includes(token))) &&
    !item.children?.some(child => !child.hidden);
  return { ...item };
};

/** A representation of dynamic content enhanced with view state props and actions. */
export class DynamicContentView {
  tabs: DynamicContentTab[];
  tabIndex?: number;

  constructor(content: DynamicContent[]) {
    this.tabs = content.map(c => new DynamicContentTab(c));
    this.tabIndex = content.length ? 0 : undefined;
  }

  static showContent(view: DynamicContentView, query: DynamicContentQuery): DynamicContentView {
    const tabIndex = view.tabs.findIndex(c => c.title === query.tabTitle);
    if (tabIndex === -1) {
      console.log(`Failed to locate a tab with title '${query.tabTitle}'.`);
      return view;
    }
    view.tabIndex = tabIndex;

    // if we don't have a specific content item to query for, return
    if (!query.contentItemTitle) return { ...view };

    // recursively find the content item if it exists, and return
    let foundItem: EnhancedDynamicContentItem | undefined = undefined;
    view.tabs[tabIndex].content.forEach((contentItem, index) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      foundItem = recursivelyFindItem(contentItem, query.contentItemTitle!);
      if (foundItem) {
        view.tabs[tabIndex].content[index] = { ...foundItem };
        view.tabs[tabIndex].showChildIndex = index;
        return false;
      }
    });
    if (!foundItem) {
      console.log(`Failed to locate a tab item with title '${query.contentItemTitle}'.`);
      return view;
    }
    return { ...view };
  }
}

/** A representation of the a dynamic content panel tab, with view state props and actions. */
export class DynamicContentTab implements DynamicContent {
  title: string;
  content: EnhancedDynamicContentItem[];
  filter?: string;
  showChildIndex?: number;
  constructor(content: DynamicContent) {
    this.title = content.title;
    this.content = content.content.map(child => new EnhancedDynamicContentItem(child));
  }

  /** Recursively Search through the given tab state, and return a new state where only items that match the query are shown. */
  static searchContent(tab: DynamicContentTab, titleQuery?: string): DynamicContentTab {
    tab.filter = titleQuery;
    if (!titleQuery) tab.content = tab.content.map(contentItem => recursivelySearchItem(contentItem));
    else {
      const titleQueryTokens = titleQuery.toLocaleLowerCase().split(" ");
      tab.content = tab.content.map(contentItem => recursivelySearchItem(contentItem, titleQueryTokens));
    }
    return { ...tab };
  }
}

/** A representation of the a dynamic content panel tab accordion item, with view state props and actions. */
export class EnhancedDynamicContentItem implements DynamicContentItem {
  id: string;
  title: string;
  keywords: string[];
  content: string[];
  children?: EnhancedDynamicContentItem[];
  showChildIndex?: number;
  hidden: boolean;

  constructor(contentItem: DynamicContentItem) {
    this.id = contentItem.id;
    this.title = contentItem.title;
    this.keywords = contentItem.keywords;
    this.content = contentItem.content;
    this.children = contentItem.children?.map(child => new EnhancedDynamicContentItem(child));
    this.hidden = false;
  }
}
